import { useEffect, useState } from "react"


import {
  Button,
  Form,
  Grid,
  Input,
  Picker,
  Row,
  SelectForm,
  Span,
} from "components"
import { MagnifyingGlassIcon } from "components/Icons"
import { ExcelIcon } from 'components/Svg'
import { useException, useLoading, useModal, useFile } from "hooks"
import moment from "moment"
import { useFormContext } from "react-hook-form"
import { IResponseTypes } from "services/core/genericEntityGST"
import {
  CentrosServices,
  WorkAccidentOccurrenceServices,
  IGetOcorrenciasByFilterParams,
  IEmpresasItens,
  EmpresasServices,
  IGenericGetAllCentro,
} from "services/GST/WorkAccidentOccurrence"
import { SPACING } from "utils"

import { IForm } from "../../types"

interface ISearchFieldsProps {
  handleSubmitForm: (_data: IForm) => void
  refreshTable: (_data?: IGetOcorrenciasByFilterParams) => void
  isFirstGet: boolean
}

const SearchForm: React.FC<ISearchFieldsProps> = ({
  handleSubmitForm,
  refreshTable,
  isFirstGet
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
  } = useFormContext<IForm>()

  const { downloadFile } = useFile()
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const { getException, createCustomException } = useException()

  const [centros, setCentros] = useState<IGenericGetAllCentro[]>([])
  const [empresasList, setEmpresasList] = useState<IEmpresasItens[]>()

  const txCpfCnpjPassaporte = watch('txCpfCnpjPassaporte')

  const nuCompetenciaInicio = watch('nuCompetenciaInicio')
  const nuCompetenciaFim = watch('nuCompetenciaFim')

  const handleGetAllCentroPlantas = (): void => {
    setLoading('loading')

    CentrosServices()
      .getAllCentroPlantas()
      .then((response) => {

        if (response?.status !== 200 || response?.data?.tipo === IResponseTypes['alert']) {
          const { tipo, detalhe, mensagem, } = response?.data

          createCustomException({
            detalhe,
            message: detalhe || mensagem || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo]
          })
        }

        setCentros(response?.data?.entity?.semValidacao)
        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error',
        })
      })
  }

  const handleGetEmpresas = () => {
    setLoading('loading')

    EmpresasServices()
      .getAllEmpresas()
      .then((response) => {
        if (
          response?.status !== 200 ||
          response?.data?.tipo === IResponseTypes['alert']
        ) {
          const { tipo, detalhe, mensagem } = response?.data

          createCustomException({
            detalhe,
            message:
              detalhe || mensagem || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo]
          })
        }

        setEmpresasList(response?.data?.entity)
        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error'
        })
      })
  }

  const handleDownloadExcel = () => {
    setLoading('loading')

    WorkAccidentOccurrenceServices()
      .generateExcel()
      .then(response => {
        if (response.status !== 200) {
          const { tipo, detalhe, mensagem } = response.data

          createCustomException({
            detalhe,
            mensagem,
            tipo: IResponseTypes[tipo],
          })
        }

        downloadFile({
          local: {
            base64: response?.data?.entity,
            filename: `Relatório Ocorrência Acidente de Trabalho - ${moment().format('DD-MM-YYYY')}.xlsx`
          }
        })
      })
      .catch((err: Error) => {
        const error = getException(err)

        showModal({
          hideButtonCancel: true,
          message: error.mensagem,
          type: error.tipo || 'error',
        })
      })
  }

  const handleResetForm = () => {
    reset()
    refreshTable({
      pageNumber: 1
    })
  }

  useEffect(() => {
    if (isFirstGet) {
      handleGetAllCentroPlantas()
      handleGetEmpresas()
    }
  }, [isFirstGet])

  return (
    <Form
      onSubmit={handleSubmit(handleSubmitForm)}
    >
      <Grid
        gridCols="4fr 1fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
      >
        <Grid
          gridCols="1fr 1fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
        >
          <Grid
            gridCols="0.4fr 0fr 0.4fr"
            gridGap={`0 ${SPACING.spacing20}`}
            margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          >
            <Picker
              control={control}
              errors={errors}
              maxDate={nuCompetenciaFim}
              label="Período (Mês/Ano)"
              formValue="nuCompetenciaInicio"
              type="monthAndYear"
            />
            <Span
              text="à"
              marginTop={SPACING.spacing30}
            />
            <Picker
              control={control}
              errors={errors}
              minDate={nuCompetenciaInicio}
              label=""
              formValue="nuCompetenciaFim"
              type="monthAndYear"
            />
          </Grid>
          <Grid
            gridCols="1fr 0fr 1fr"
            gridGap={`0 ${SPACING.spacing20}`}
            margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          >
            <SelectForm
              defaultValue={undefined}
              control={control}
              errors={errors}
              label="Empresa"
              placeholder="Selecione"
              clearSelect={true}
              formValue="txEmpresa"
              listOptions={
                empresasList?.map((empresa: IEmpresasItens) => (
                  { label: empresa?.nmEmpresa, value: empresa?.nmEmpresa }
                )) || []
              }
            />
            <div />
            <SelectForm
              defaultValue={undefined}
              control={control}
              errors={errors}
              label="Centro"
              placeholder="Selecione"
              clearSelect={true}
              formValue="idCentroPlanta"
              listOptions={
                centros?.map((centro: IGenericGetAllCentro) => (
                  { label: centro?.txDescricaoCentroPlanta, value: centro?.idCentroPlanta }
                )) || []
              }
            />
          </Grid>
          <Grid
            gridCols="0.4fr 0fr 0.4fr"
            gridGap={`0 ${SPACING.spacing20}`}
            margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          >
            <SelectForm
              control={control}
              errors={errors}
              label="Tipo"
              formValue="cdTipoFornecedor"
              allObject={true}
              listOptions={[
                { label: "Pessoa Física", value: 0 },
                { label: "Pessoa Jurídica", value: 1 },
                { label: "Pessoa Estrangeira", value: 2 },
              ]}
              clearSelect={true}
            />
            <div />
            <Input
              control={control}
              errors={errors}
              label="CPF/CNPJ/Cód.Fornecedor"
              placeholder="Nº"
              formValue="txCpfCnpjPassaporte"
              mask={
                isNaN(parseInt(txCpfCnpjPassaporte))
                  ? ['############']
                  : ['###.###.###-##', '##.###.###/####-##']
              }
            />
          </Grid>
          <Grid
            gridCols="1fr 1fr"
            gridGap={`0 ${SPACING.spacing20}`}
            margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
          >
            <Input
              control={control}
              errors={errors}
              label="Fornecedor"
              placeholder="Fornecedor"
              formValue="txFornecedor"
            />
            <div />
          </Grid>
        </Grid>
        <Grid
          gridCols="1fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
        >
          <Row justifyContent="end" alignItems={"start"} marginTop={SPACING.spacing15}>
            <ExcelIcon style={{ cursor: 'pointer' }} onClick={handleDownloadExcel} />
          </Row>
        </Grid>
      </Grid>
      <div style={{ height: SPACING.spacing20 }}></div>
      <Row justifyContent="flex-end">
        <Button
          variantButton="text"
          text="LIMPAR"
          width={'max-content'}
          onClick={handleResetForm}
          marginRight={SPACING.spacing20}
        />
        <Button
          endIcon={<MagnifyingGlassIcon />}
          type="submit"
          text="PESQUISAR"
        />
      </Row>
    </Form>
  )
}

export default SearchForm
